import React, { useEffect, useMemo, useState } from 'react';
import { Mail, RefreshCw, Search, CheckCircle2, XCircle, Clock, Send, Eye, MousePointerClick, AlertTriangle, Loader2, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  try {
    const a = JSON.parse(localStorage.getItem('dhs-admin') || '{}');
    return a?.token ? { Authorization: `Bearer ${a.token}` } : {};
  } catch { return {}; }
};

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className={`rounded-2xl p-4 border border-[#1F0068]/5 ${tone}`}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold opacity-80"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <div className="mt-1 text-2xl font-extrabold">{value ?? 0}</div>
  </div>
);

const statusBadge = (row) => {
  const status = row.last_status || row.status;
  const map = {
    sent: { label: 'Sent', cls: 'bg-blue-100 text-blue-700', Icon: Send },
    delivered: { label: 'Delivered', cls: 'bg-[#00b67a]/15 text-[#00b67a]', Icon: CheckCircle2 },
    opened: { label: 'Opened', cls: 'bg-[#1F0068]/10 text-[#1F0068]', Icon: Eye },
    clicked: { label: 'Clicked', cls: 'bg-[#FFEDD6] text-[#FF5C39]', Icon: MousePointerClick },
    bounced: { label: 'Bounced', cls: 'bg-red-100 text-red-700', Icon: XCircle },
    complained: { label: 'Complained', cls: 'bg-red-100 text-red-700', Icon: AlertTriangle },
    failed: { label: 'Failed', cls: 'bg-red-100 text-red-700', Icon: XCircle },
    queued_no_key: { label: 'Queued', cls: 'bg-yellow-100 text-yellow-800', Icon: Clock },
    pending: { label: 'Pending', cls: 'bg-yellow-100 text-yellow-800', Icon: Clock },
  };
  const cfg = map[status] || map.sent;
  const Icon = cfg.Icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${cfg.cls}`}><Icon className="w-3 h-3" /> {cfg.label}</span>;
};

const AdminEmails = () => {
  const { toast } = useToast();
  const [data, setData] = useState({ rows: [], total: 0, stats: {}, tags: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [tag, setTag] = useState('all');
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState('');
  const [selected, setSelected] = useState(null);
  const LIMIT = 25;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), skip: String(page * LIMIT) });
      if (q.trim()) params.set('q', q.trim());
      if (status !== 'all') params.set('status', status);
      if (tag !== 'all') params.set('tag', tag);
      const res = await fetch(`${BACKEND}/api/admin/emails?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData(j);
    } catch (e) {
      toast({ title: 'Could not load email log', description: String(e?.message || e) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, tag, page]);

  const refresh = async (row) => {
    if (!row.provider_id) return toast({ title: 'No provider ID', description: 'This email was queued locally and never sent to Resend.' });
    setBusy(row.id);
    try {
      const res = await fetch(`${BACKEND}/api/admin/emails/${row.id}/refresh`, { method: 'POST', headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) return toast({ title: 'Refresh failed', description: j.detail || 'Please try again in a moment.' });
      // Replace row in place
      setData((prev) => ({ ...prev, rows: prev.rows.map((r) => (r.id === row.id ? j : r)) }));
      toast({ title: 'Status updated', description: `${row.to} → ${j.last_status || j.status}` });
    } finally { setBusy(''); }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  const preview = useMemo(() => selected && data.rows.find((r) => r.id === selected), [selected, data]);

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1F0068] tracking-tight">Email log</h1>
          <p className="text-xs text-[#1F0068]/60 mt-1">Every email the platform has sent, with live delivery status refreshed from Resend on demand.</p>
        </div>
        <Button data-testid="admin-emails-reload" variant="outline" onClick={load} disabled={loading} className="rounded-full gap-1.5">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reload
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard icon={Mail} label="Total" value={data.stats?.total} tone="bg-white text-[#1F0068]" />
        <StatCard icon={Send} label="Sent" value={data.stats?.sent} tone="bg-blue-50 text-blue-700" />
        <StatCard icon={CheckCircle2} label="Delivered*" value={data.rows.filter((r) => (r.last_status || '') === 'delivered').length} tone="bg-[#00b67a]/10 text-[#00b67a]" />
        <StatCard icon={Eye} label="Opened*" value={data.rows.filter((r) => r.opened_at).length} tone="bg-[#1F0068]/5 text-[#1F0068]" />
        <StatCard icon={XCircle} label="Bounced / Failed" value={(data.stats?.failed || 0) + data.rows.filter((r) => (r.last_status || '') === 'bounced').length} tone="bg-red-50 text-red-700" />
      </div>
      <p className="text-[10px] text-[#1F0068]/50 -mt-4 mb-4">* Delivered / Opened counts reflect only rows whose status has been refreshed from Resend. Click ↻ on any row to fetch its latest event.</p>

      <div className="bg-white rounded-2xl border border-[#1F0068]/5 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F0068]/50" />
          <Input data-testid="admin-emails-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setPage(0), load())} placeholder="Search recipient, subject, sender, or tag…" className="h-10 pl-9 rounded-full bg-[#FFF6EB] border-[#1F0068]/10" />
        </div>
        <select data-testid="admin-emails-status" value={status} onChange={(e) => { setPage(0); setStatus(e.target.value); }} className="h-10 px-3 rounded-full text-sm bg-white border border-[#1F0068]/10 text-[#1F0068]">
          <option value="all">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="queued_no_key">Queued (no key)</option>
          <option value="pending">Pending</option>
        </select>
        <select data-testid="admin-emails-tag" value={tag} onChange={(e) => { setPage(0); setTag(e.target.value); }} className="h-10 px-3 rounded-full text-sm bg-white border border-[#1F0068]/10 text-[#1F0068] max-w-[180px]">
          <option value="all">All tags</option>
          {(data.tags || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <Button data-testid="admin-emails-apply" onClick={() => { setPage(0); load(); }} className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white">Apply</Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#1F0068]/5 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#1F0068]/60 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading email log…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FFF6EB] text-[#1F0068]/70 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Sent</th>
                  <th className="text-left px-5 py-3">Recipient</th>
                  <th className="text-left px-5 py-3">Subject</th>
                  <th className="text-left px-5 py-3">Tag</th>
                  <th className="text-left px-5 py-3">By</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1F0068]/5">
                {(data.rows || []).map((r) => (
                  <tr key={r.id} data-testid={`email-row-${r.id}`} className="hover:bg-[#FFF6EB]">
                    <td className="px-5 py-3 text-[#1F0068]/70 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString('en-GB') : '—'}</td>
                    <td className="px-5 py-3 text-[#1F0068] font-semibold">
                      {r.to}
                      {(r.cc?.length || 0) + (r.bcc?.length || 0) > 0 && (
                        <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-[#1F0068]/60 font-normal" title={`CC: ${(r.cc || []).join(', ') || '—'} · BCC: ${(r.bcc || []).join(', ') || '—'}`}><Paperclip className="w-3 h-3" /> +{(r.cc?.length || 0) + (r.bcc?.length || 0)}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-[#1F0068]/80 max-w-[280px] truncate">{r.subject}</td>
                    <td className="px-5 py-3 text-[#1F0068]/70 text-xs">{r.tag || '—'}</td>
                    <td className="px-5 py-3 text-[#1F0068]/70 text-xs">{r.sent_by || '—'}<div className="text-[10px] text-[#1F0068]/50">{r.sent_by_role || ''}</div></td>
                    <td className="px-5 py-3">{statusBadge(r)}
                      {r.opened_at && <div className="text-[10px] text-[#1F0068]/50 mt-0.5">Opened {new Date(r.opened_at).toLocaleString('en-GB')}</div>}
                      {r.clicked_at && <div className="text-[10px] text-[#FF5C39] mt-0.5">Clicked {new Date(r.clicked_at).toLocaleString('en-GB')}</div>}
                    </td>
                    <td className="px-5 py-3 text-right space-x-1 whitespace-nowrap">
                      <Button data-testid={`refresh-email-${r.id}`} size="sm" variant="outline" onClick={() => refresh(r)} disabled={busy === r.id || !r.provider_id} title={r.provider_id ? 'Refresh delivery status from Resend' : 'This email has no Resend id'} className="rounded-full text-xs">
                        {busy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      </Button>
                      <Button data-testid={`view-email-${r.id}`} size="sm" variant="outline" onClick={() => setSelected(r.id)} className="rounded-full text-xs">Details</Button>
                    </td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan="7" className="px-5 py-10 text-center text-[#1F0068]/60">No emails match this filter.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-xs text-[#1F0068]/70">
          <span>Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} className="rounded-full">Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} className="rounded-full">Next</Button>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">Email detail</div>
                <h2 className="text-lg font-bold text-[#1F0068] mt-1 break-all">{preview.subject}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#1F0068]/60">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-[#1F0068]/80">
              <div><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">To</div><div className="font-semibold break-all">{preview.to}</div></div>
              <div><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">Status</div><div>{statusBadge(preview)}</div></div>
              {preview.cc?.length > 0 && <div className="col-span-2"><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">CC</div><div>{preview.cc.join(', ')}</div></div>}
              {preview.bcc?.length > 0 && <div className="col-span-2"><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">BCC</div><div>{preview.bcc.join(', ')}</div></div>}
              <div><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">Sent by</div><div>{preview.sent_by || '—'} <span className="opacity-60">({preview.sent_by_role})</span></div></div>
              <div><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">Tag</div><div>{preview.tag || '—'}</div></div>
              <div><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">Created</div><div>{new Date(preview.created_at).toLocaleString('en-GB')}</div></div>
              <div><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">Attachments</div><div>{preview.attachment_count || 0}</div></div>
              {preview.provider_id && <div className="col-span-2"><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">Resend ID</div><div className="font-mono text-[10px] break-all">{preview.provider_id}</div></div>}
              {preview.opened_at && <div className="col-span-2"><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">Opened</div><div>{new Date(preview.opened_at).toLocaleString('en-GB')}</div></div>}
              {preview.clicked_at && <div className="col-span-2"><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">Clicked</div><div>{new Date(preview.clicked_at).toLocaleString('en-GB')}</div></div>}
              {preview.bounced_at && <div className="col-span-2"><div className="uppercase text-[10px] font-bold text-[#1F0068]/50 tracking-widest">Bounced</div><div>{new Date(preview.bounced_at).toLocaleString('en-GB')}</div></div>}
              {preview.error && <div className="col-span-2 text-red-600"><div className="uppercase text-[10px] font-bold text-red-700 tracking-widest">Error</div><div>{preview.error}</div></div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              {preview.provider_id && <Button onClick={() => refresh(preview)} disabled={busy === preview.id} className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white gap-1.5">{busy === preview.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh from Resend</Button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminEmails;
